import React, { useMemo } from 'react';
import { getIn, useFormikContext } from 'formik';

import MultiSelectField from '~/components/core/Molecules/Fields/MultiSelectField';

import type { MoiMethod } from './types';

export const MOI_METHODS_FILTER_FIELD_ID = 'moi_method_keys';

interface MoiEmailMethodsFilterFormikProps {
  methods: MoiMethod[];
  disabled?: boolean;
  optionsLoading?: boolean;
}

/**
 * The search section's MOI-method filter: a plain multi-select (checkbox rows) over the org's
 * methods. Unlike the builder's mapping field there is no mapped-elsewhere disable logic — any
 * method is selectable for search, and the backend ORs the selected keys.
 */
const MoiEmailMethodsFilterFormik: React.FC<MoiEmailMethodsFilterFormikProps> = ({
  methods,
  disabled = false,
  optionsLoading = false,
}) => {
  const { values, setFieldValue } = useFormikContext<Record<string, unknown>>();
  const selectedKeys = (getIn(values, MOI_METHODS_FILTER_FIELD_ID) as string[] | undefined) ?? [];

  const displayNameByKey = useMemo(() => {
    const dict: Record<string, string> = {};
    methods.forEach((method) => {
      dict[method.key] = method.display_name;
    });
    return dict;
  }, [methods]);

  // Sort a copy by display name (MultiSelectField's `sortAlphabetic` would mutate this array in place).
  const options = useMemo(
    () =>
      methods
        .map((method) => method.key)
        .sort((a, b) => (displayNameByKey[a] ?? a).localeCompare(displayNameByKey[b] ?? b)),
    [methods, displayNameByKey]
  );

  return (
    <MultiSelectField<string>
      id={MOI_METHODS_FILTER_FIELD_ID}
      label="MOI Methods"
      fullWidth
      value={selectedKeys}
      options={options}
      disabled={disabled}
      optionsLoading={optionsLoading}
      autoSelectSingleOption={false}
      onChange={(newValues) => setFieldValue(MOI_METHODS_FILTER_FIELD_ID, newValues)}
      renderValue={(selected) => selected.map((key) => displayNameByKey[key] ?? key).join(', ')}
      renderOption={(key) => displayNameByKey[key] ?? key}
    />
  );
};

export default MoiEmailMethodsFilterFormik;
